// Saving an authed file response to disk.
//
// The blob's MIME type outranks the download attribute's extension on Android:
// the browser has to hand DownloadManager a type, and if that type's canonical
// extension differs from the filename's, Android appends it. A .dwg begins with
// the ASCII magic "AC1032", so an untyped blob got sniffed as text/plain and
// users ended up with "drawing.dwg.txt".
//
// application/octet-stream is the "unknown binary" sentinel — nothing is
// appended and the filename stands. We fall back to it whenever the declared
// type looks untrustworthy, which also makes this correct while the API still
// derives Content-Type by sniffing content instead of the filename.

// Extensions where a text/* content type is genuinely right, so leaving the
// server's type alone can't cost us the extension.
const TEXT_EXTENSIONS = [
  '.txt', '.csv', '.log', '.md', '.json', '.xml', '.html', '.htm', '.css', '.js',
];

function extensionOf(filename) {
  const dot = `${filename}`.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
}

// A text/* type on a file that isn't a text file is the signature of content
// sniffing gone wrong — that's the case that costs us the extension. A missing
// type is no better, since the browser just sniffs it itself.
function isTrustworthyType(type, filename) {
  if (!type) return false;
  if (type.startsWith('text/')) return TEXT_EXTENSIONS.includes(extensionOf(filename));
  return true;
}

/**
 * Save an axios `responseType: 'blob'` response to disk under `filename`.
 *
 * @param {Blob} blob  `response.data` from an axios blob request.
 * @param {string} filename  Name to save as; its extension is authoritative.
 */
export function downloadBlob(blob, filename) {
  const type = isTrustworthyType(blob.type, filename)
    ? blob.type
    : 'application/octet-stream';

  const url = URL.createObjectURL(new Blob([blob], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoking synchronously can truncate the download on some mobile browsers;
  // TaskEmailView.openAttachment defers for the same reason.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
